import { Flex } from "@chakra-ui/react";
import { useRef, useCallback } from "react";
import { format } from "date-fns";
import CDS from "design-system";
import { parseDateOnly } from "helpers/time";
import { Task } from "features/Resources/Task/models";

interface TaskStartFieldProps {
  task: Pick<Task, "startAfterDependencyId" | "startImmediately" | "startedAt" | "startOn">;
  startedAtDisplay?: string | null;
  onChangeStartOn?: (date: Date) => void;
}

export default function TaskStartField({ task, startedAtDisplay, onChangeStartOn }: TaskStartFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handlePillClick = useCallback(() => {
    const input = inputRef.current;
    if (!input) return;
    if (typeof (input as HTMLInputElement & { showPicker?: () => void }).showPicker === "function") {
      (input as HTMLInputElement & { showPicker: () => void }).showPicker();
    } else {
      input.click();
    }
  }, []);

  if (task.startAfterDependencyId) {
    return <CDS.FieldTag label="Starts After">Dependency</CDS.FieldTag>;
  }

  if (task.startImmediately && !task.startedAt) {
    return <CDS.FieldTag label="Starts">Immediately</CDS.FieldTag>;
  }

  if (task.startedAt) {
    const display = startedAtDisplay
      ?? format(new Date(String(task.startedAt)), "MMM d, yyyy");
    return <CDS.FieldTag label="Started">{display}</CDS.FieldTag>;
  }

  if (task.startOn) {
    if (!onChangeStartOn) {
      return (
        <CDS.FieldTag label="Starts">
          {format(parseDateOnly(task.startOn), "MMM d, yyyy")}
        </CDS.FieldTag>
      );
    }

    return (
      <CDS.BasePill onClick={handlePillClick} position="relative">
        <Flex direction="column" gap="0">
          <CDS.Text10 color="content.tertiary" fontWeight="500" lineHeight="1.3">
            Starts
          </CDS.Text10>
          <CDS.Text12 fontWeight="700" color="content.primary" lineHeight="1.3">
            {format(parseDateOnly(task.startOn), "MMM d, yyyy")}
          </CDS.Text12>
        </Flex>
        <input
          ref={inputRef}
          type="date"
          style={{ position: "absolute", opacity: 0, width: 0, height: 0, top: 0, left: 0, pointerEvents: "none" }}
          value={format(parseDateOnly(task.startOn), "yyyy-MM-dd")}
          onChange={(e) => {
            if (e.target.value) {
              const [year, month, day] = e.target.value.split("-").map(Number);
              onChangeStartOn(new Date(year, month - 1, day));
            }
          }}
        />
      </CDS.BasePill>
    );
  }

  return null;
}
